import React, { useEffect, useState } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';

interface ReferralStat {
    referalCode: string;
    couponType: string;
    fixedValue: number;
    userCount: number;
    leadCount: number;
    totalDue: number;
    side: 'buyer' | 'seller';
}

export default function BackOfficeReferralStats() {
    const [stats, setStats] = useState<ReferralStat[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${CONFIG.apiUrl}/admin/referral-stats`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setStats(response.data.data);
            } catch (error) {
                console.error('Error fetching referral stats:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    const grandTotal = stats.reduce((sum, s) => sum + s.totalDue, 0);

    return (
        <div className="pt-[50px]">
            <Card extra="p-5">
                <div className="mb-5 flex items-center justify-between gap-2">
                    <div className="text-2xl font-bold text-navy-700 dark:text-white">
                        Stats affiliations
                    </div>
                    {!loading && stats.length > 0 && (
                        <div className="text-sm font-semibold text-navy-700 dark:text-white">
                            Total dû : <span className="text-brand-500">{grandTotal.toFixed(2)} €</span>
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="py-10 text-center text-gray-500">Chargement...</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-gray-200 dark:border-navy-600">
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Code d'affiliation
                                    </th>
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Côté
                                    </th>
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Type
                                    </th>
                                    <th className="pb-3 text-right text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Valeur / lead
                                    </th>
                                    <th className="pb-3 text-right text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Utilisateurs
                                    </th>
                                    <th className="pb-3 text-right text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Leads payés
                                    </th>
                                    <th className="pb-3 text-right text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Total dû
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {stats.map((s) => (
                                    <tr key={s.referalCode} className="border-b border-gray-100 dark:border-navy-700">
                                        <td className="py-4 font-mono text-sm font-semibold text-navy-700 dark:text-white">
                                            {s.referalCode}
                                        </td>
                                        <td className="py-4 text-sm">
                                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${s.side === 'seller' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                                {s.side === 'seller' ? 'Vendeur' : 'Acheteur'}
                                            </span>
                                        </td>
                                        <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                                            {s.couponType ?? '—'}
                                        </td>
                                        <td className="py-4 text-right text-sm text-gray-600 dark:text-gray-400">
                                            {s.fixedValue != null ? `${s.fixedValue} €` : '—'}
                                        </td>
                                        <td className="py-4 text-right text-sm text-gray-600 dark:text-gray-400">
                                            {s.userCount}
                                        </td>
                                        <td className="py-4 text-right text-sm text-gray-600 dark:text-gray-400">
                                            {s.leadCount}
                                        </td>
                                        <td className="py-4 text-right text-sm font-semibold text-navy-700 dark:text-white">
                                            {s.totalDue.toFixed(2)} €
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {stats.length === 0 && (
                            <div className="py-10 text-center text-gray-500">
                                Aucune donnée d'affiliation
                            </div>
                        )}
                    </div>
                )}
            </Card>
        </div>
    );
}
